import { useChangeCampaignStateMutation } from 'modules/campaigns/lib';
import { CampaignState } from 'modules/domains/campaign';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { ModalType, useCampaignContext, useModalContext } from '../../context';
import { useCancelCampaignAlerts } from '../../hooks/useCancelCampaignAlerts';
import { useGenerateCampaignAlerts } from '../../hooks/useGenerateCampaignAlerts';
import { maybeRestErrorMessage } from '../../utils';
import { getChangeCampaignStatePayload } from './ChangeCampaignStateModal.utils';

export const useChangeCampaignState = () => {
  const { closeModal } = useModalContext();
  const { isLoading, mutate, error } = useChangeCampaignStateMutation();
  const { generateCampaignAlerts } = useGenerateCampaignAlerts();
  const { cancelCampaignAlerts } = useCancelCampaignAlerts();

  const dispatch = useDispatch();
  const {
    campaign,
    campaign: { campaignAlertsEnabled, id, name },
    refetch: refetchCampaign,
  } = useCampaignContext();

  const changeCampaignState = (campaignState?: CampaignState) => {
    if (!campaignState) return;

    const onSuccess = () => {
      if (campaignState === CampaignState.Running) {
        generateCampaignAlerts({ campaignAlertsEnabled, id });
      } else if (campaignState === CampaignState.Finished) {
        cancelCampaignAlerts({
          campaignIds: [id],
          campaignAlertsEnabled,
        });
      }

      dispatch(addPositiveToast(`${name} changed to ${campaignState}.`));
      closeModal(ModalType.ChangeCampaignState);

      refetchCampaign();
    };

    const onError = (e: unknown) => {
      const errorResponseMessage = maybeRestErrorMessage(e);
      const defaultErrorMessage = 'Error changing campaign state: Something went wrong.';
      const errorMessage = errorResponseMessage || defaultErrorMessage;
      dispatch(addNegativeToastWithErrorId(errorMessage, 'changeCampaignState', e));
    };

    const payload = getChangeCampaignStatePayload(campaign, campaignState);

    mutate(payload, {
      onSuccess,
      onError,
    });
  };

  return { isLoading, error, changeCampaignState };
};
